import { useCallback, useEffect, useState } from 'react'
import { fileDownloadHandler as downloadFn } from '../utilities/downloadHandler'

/**
 * Rather magic hook that expects a useQuery hook which accepts typical useQuery options
 * as the final arg (which is the convention), and returns a hook which wraps that hook
 * with "download" type behavior, adding a `download` function to the typical useQuery
 * response which will automatically create a download for the data on success using
 * the download util function.
 *
 * @param {function} useQuery a useQuery-like function accepting useQuery options as
 *   the last arg, and returns useQuery output
 * @param {object} [staticOptions={}] default options passed to useQuery as well as the
 *   contentType and filename for the download behavior
 * @param {object} [staticOptions.contentType] the contentType for the download, required either
 *   in static options or on hook invocation
 * @param {object} [staticOptions.filename=download] the filename for the download, may be
 *   given in static options or on hook invocation
 */
export const createUseDownloadQuery = (useQuery, staticOptions = {}) => {
  // pull contentType & filename out of the static options
  const {
    filename: staticFilename,
    contentType: staticContentType,
    ...staticQueryOptions
  } = staticOptions

  return (...args) => {
    // This bit is a little fragile, it assumes the last arg that's an object which is
    // passed to a query hook will be the query options, and depends on that convention
    const argsOptions =
      (typeof args[args.length - 1] === 'object' && args.pop()) || {}

    // pull contentType & filename out of the given hook options
    const {
      filename: argsFilename,
      contentType: argsContentType,
      enabled: maybeArgsEnabled,
      ...argsQueryOptions
    } = argsOptions

    // derive the filename & contentype, respecting the hook options, falling back to static
    const filename = argsFilename || staticFilename || 'download'
    const contentType = argsContentType || staticContentType
    const argsEnabled = maybeArgsEnabled !== false

    if (!contentType) {
      throw new Error('download queries must specify contentType option on create or at invocation')
    }

    const [downloadInitiated, setDownloadInitiated] = useState(false)
    const queryEnabled = argsEnabled && downloadInitiated

    const { data, ...queryResult } = useQuery(...args, {
      // set the staleTime to 5 minutes by default, but allow override via static or arg options
      staleTime: 1000 * 60 * 5,
      // mix in static hook generation options and options passed to the hook on call
      ...staticQueryOptions,
      ...argsQueryOptions,
      // NOTE disable all refetch, or this will end up re-downloading on scenarios like window
      // refocus, etc. This problem probably means this hook needs work. We want to rely on
      // react-query for the cache, but the fact that it's hook based & baked with re-render logic
      // makes this tricky. Because it's hook based, another issue is that in dropdowns, the
      // download won't work if the dropdown closes immediately, unmounting the component/hook.
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      // finally the query is enabled when the download is initiated, and enabled: false is not
      // passed in hook options
      enabled: queryEnabled,
    })

    const downloadData = useCallback(data => {
      downloadFn(data, filename, contentType)
    }, [filename, contentType])

    // the download function, which downloads data immediately if it's cached or already present,
    // or triggers the hook to fetch if it has not yet by initiating the download which enables
    // the query hook.
    const download = useCallback(() => {
      if (!argsEnabled) {
        return
      }
      if (data) {
        downloadData(data)
      } else {
        setDownloadInitiated(true)
      }
    }, [argsEnabled, data, downloadData, setDownloadInitiated])

    // if the query is enabled and the data changes, download the data. This is the part of the
    // hook that makes download feel a little uncertain, as if the content type, filename, or
    // data changes (including refetches where the data content doesn't really change), then we'll
    // see a download here without pressing the button. This makes this hook feel less than ideal.
    useEffect(() => {
      if (queryEnabled && data !== undefined) {
        downloadData(data)
      }
    }, [data, downloadData, queryEnabled])

    return { ...queryResult, data, download }
  }
}
